import Pusher from 'pusher-js';
import api from '../api/axiosClient';

/**
 * The realtime connection, and the names of the things worth listening to.
 *
 * Every channel that carries a restaurant's data is private: the app key is
 * readable in any browser's devtools and the channel names are sequential ids,
 * so without authorisation `restaurant.2` would hand a stranger somebody
 * else's guests, phone numbers and all. Subscribing to one now costs a round
 * trip to /api/broadcasting/auth, which answers only for the restaurant the
 * bearer token belongs to (see backend/routes/channels.php).
 *
 * The seat grid stays public on purpose — seat ids and their status, no PII,
 * and the guest looking at it has no token to authorise with.
 *
 * The server on the other end is either our own Reverb or Pusher's hosted one.
 * They speak the same protocol, so the library and every subscriber here are
 * identical either way; only where it dials changes. Set VITE_WS_HOST and it
 * goes to ours, leave it unset and it goes to Pusher — which makes switching an
 * environment, or switching back, a matter of one variable and no code.
 */

let pusherInstance = null;

/** Our own server when a host is named, Pusher's cloud when one is not. */
function transportOptions() {
  const host = import.meta.env.VITE_WS_HOST;

  if (!host) {
    return { cluster: import.meta.env.VITE_PUSHER_CLUSTER, forceTLS: true };
  }

  const port = Number(import.meta.env.VITE_WS_PORT ?? 443);
  // Plain ws only where it is plainly local. Anywhere else a mistyped port must
  // not silently downgrade a merchant's guest list onto an unencrypted socket.
  const secure = String(import.meta.env.VITE_WS_TLS ?? 'true') === 'true';

  return {
    wsHost:  host,
    wsPort:  port,
    wssPort: port,
    forceTLS: secure,
    enabledTransports: ['ws', 'wss'],
    // Pusher's telemetry endpoint, which our server does not have and which
    // would otherwise be called on every connection.
    disableStats: true,
    // Unused once wsHost is set, but the library still expects the key to be
    // present in its options.
    cluster: import.meta.env.VITE_PUSHER_CLUSTER ?? 'mt1',
  };
}

export const getPusher = () => {
  if (!pusherInstance) {
    pusherInstance = new Pusher(import.meta.env.VITE_PUSHER_KEY, {
      ...transportOptions(),
      channelAuthorization: {
        // Authorised through the app's own client rather than by handing Pusher
        // a set of headers.
        //
        // Headers given here are read once, when this instance is built, and
        // then kept for its lifetime — but an access token lasts thirty minutes
        // and the axios interceptor quietly replaces it. The connection would
        // go on presenting the token it was born with, and every subscription
        // after the first refresh would 401 for no visible reason.
        //
        // Going through `api` means each authorisation carries whatever is
        // current, and inherits the refresh-and-retry that every other call
        // already gets.
        customHandler: async ({ socketId, channelName }, callback) => {
          try {
            const res = await api.post('/broadcasting/auth', {
              socket_id: socketId,
              channel_name: channelName
            });

            callback(null, res.data);
          } catch (error) {
            callback(error, null);
          }
        }
      }
    });
  }

  return pusherInstance;
};

/**
 * Dropped on sign-out. The instance caches the token it was built with, so a
 * connection made by the last person would keep authorising as them — and the
 * next sign-in would subscribe to the previous session's channels.
 */
export const resetPusher = () => {
  if (pusherInstance) {
    pusherInstance.disconnect();
    pusherInstance = null;
  }
};

/**
 * One place both this app and the mobile app read the names from. A private
 * channel is addressed with its `private-` prefix; the authorisation callback
 * on the server sees the name without it.
 */
export const channels = {
  /**
   * Everything scoped to the business rather than to a branch:
   * `booking.created`, `booking.status.changed`, `dashboard.refresh`, and
   * `menu.stock.changed` — POS stock is one pool per restaurant per day, not
   * one per location.
   */
  restaurant: (restaurantId) => `private-restaurant.${restaurantId}`,
  user: (userId) => `private-user.${userId}`,
  kitchen: (restaurantId, locationId) => `private-kitchen.${restaurantId}.${locationId}`,
  /** The floor plan at one branch: `table.status.changed`. */
  floor: (restaurantId, locationId) => `private-floor.${restaurantId}.${locationId}`,
  /** Public: the seat grid a guest sees before signing in. */
  ticketSession: (sessionId) => `ticket-session.${sessionId}`
};
